import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

// Types
export interface PersonalRoom {
  id: string;
  name: string;
  owner_id: string;
  created_at: string;
  updated_at: string;
  invited_users: string[];
}

export interface PersonalSubRoom {
  id: string;
  room_id: string;
  name: string;
  type: 'chat' | 'voice' | 'media';
  position: number;
  created_at: string;
}

export interface PersonalRoomMessage {
  id: string;
  sub_room_id: string;
  user_id: string;
  content: string;
  created_at: string;
  // Joined profile data
  profiles?: {
    username: string;
    avatar_url: string | null;
  };
}

interface UsePersonalRoomsReturn {
  // State
  myRoom: PersonalRoom | null;
  invitedRooms: PersonalRoom[];
  subRooms: PersonalSubRoom[];
  loading: boolean;
  error: string | null;

  // Room operations
  createRoom: (name: string) => Promise<string | null>;
  updateRoom: (roomId: string, name: string) => Promise<boolean>;
  deleteRoom: (roomId: string) => Promise<boolean>;

  // Sub-room operations
  loadSubRooms: (roomId: string) => Promise<void>;
  addSubRoom: (roomId: string, name: string, type: 'chat' | 'voice' | 'media') => Promise<boolean>;
  removeSubRoom: (subRoomId: string) => Promise<boolean>;

  // Invite operations
  inviteUser: (roomId: string, userId: string) => Promise<boolean>;
  removeInvite: (roomId: string, userId: string) => Promise<boolean>;

  // Messages
  sendMessage: (subRoomId: string, content: string) => Promise<boolean>;
  loadMessages: (subRoomId: string, limit?: number) => Promise<PersonalRoomMessage[]>;

  // Refresh
  refresh: () => Promise<void>;
}

export function usePersonalRooms(userId: string | null): UsePersonalRoomsReturn {
  const [myRoom, setMyRoom] = useState<PersonalRoom | null>(null);
  const [invitedRooms, setInvitedRooms] = useState<PersonalRoom[]>([]);
  const [subRooms, setSubRooms] = useState<PersonalSubRoom[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load user's personal room and invited rooms
  const loadRooms = useCallback(async () => {
    if (!userId) {
      setMyRoom(null);
      setInvitedRooms([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Load user's own room
      const { data: ownRoom, error: ownError } = await supabase
        .from('personal_rooms')
        .select('*')
        .eq('owner_id', userId)
        .maybeSingle();

      if (ownError && ownError.code !== 'PGRST116') {
        console.error('Error loading own room:', ownError);
        setError('Failed to load your room');
      } else {
        setMyRoom(ownRoom);
      }

      // Load rooms user is invited to
      const { data: invited, error: invitedError } = await supabase
        .from('personal_rooms')
        .select('*')
        .contains('invited_users', [userId]);

      if (invitedError) {
        console.error('Error loading invited rooms:', invitedError);
      } else {
        setInvitedRooms(invited || []);
      }
    } catch (err) {
      console.error('Error in loadRooms:', err);
      setError('Failed to load rooms');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Initial load
  useEffect(() => {
    loadRooms();
  }, [loadRooms]);

  // Create a new personal room with default sub-rooms
  const createRoom = useCallback(async (name: string): Promise<string | null> => {
    if (!userId) return null;

    try {
      // First, check if user already has a room
      const { data: existing } = await supabase
        .from('personal_rooms')
        .select('id')
        .eq('owner_id', userId)
        .maybeSingle();

      if (existing) {
        setError('You already have a personal room');
        return null;
      }

      // Create the room
      const { data: newRoom, error: roomError } = await supabase
        .from('personal_rooms')
        .insert({
          name,
          owner_id: userId,
          invited_users: [],
        })
        .select()
        .single();

      if (roomError) {
        console.error('Error creating room:', roomError);
        setError(roomError.message);
        return null;
      }

      // Create default sub-rooms (chat and voice lounge)
      const defaultSubRooms = [
        { room_id: newRoom.id, name: 'chat', type: 'chat', position: 0 },
        { room_id: newRoom.id, name: 'lounge', type: 'voice', position: 1 },
      ];

      const { error: subRoomError } = await supabase
        .from('personal_sub_rooms')
        .insert(defaultSubRooms);

      if (subRoomError) {
        console.error('Error creating sub-rooms:', subRoomError);
        // Room was created, just warn about sub-rooms
      }

      // Refresh rooms list
      await loadRooms();
      return newRoom.id;
    } catch (err) {
      console.error('Error in createRoom:', err);
      setError('Failed to create room');
      return null;
    }
  }, [userId, loadRooms]);

  // Update room name
  const updateRoom = useCallback(async (roomId: string, name: string): Promise<boolean> => {
    try {
      const { error: updateError } = await supabase
        .from('personal_rooms')
        .update({ name, updated_at: new Date().toISOString() })
        .eq('id', roomId);

      if (updateError) {
        console.error('Error updating room:', updateError);
        setError(updateError.message);
        return false;
      }

      await loadRooms();
      return true;
    } catch (err) {
      console.error('Error in updateRoom:', err);
      return false;
    }
  }, [loadRooms]);

  // Delete room
  const deleteRoom = useCallback(async (roomId: string): Promise<boolean> => {
    try {
      const { error: deleteError } = await supabase
        .from('personal_rooms')
        .delete()
        .eq('id', roomId);

      if (deleteError) {
        console.error('Error deleting room:', deleteError);
        setError(deleteError.message);
        return false;
      }

      setMyRoom(null);
      setSubRooms([]);
      return true;
    } catch (err) {
      console.error('Error in deleteRoom:', err);
      return false;
    }
  }, []);

  // Load sub-rooms for a room
  const loadSubRooms = useCallback(async (roomId: string): Promise<void> => {
    try {
      const { data, error: subError } = await supabase
        .from('personal_sub_rooms')
        .select('*')
        .eq('room_id', roomId)
        .order('position', { ascending: true });

      if (subError) {
        console.error('Error loading sub-rooms:', subError);
        return;
      }

      setSubRooms(data || []);
    } catch (err) {
      console.error('Error in loadSubRooms:', err);
    }
  }, []);

  // Add a sub-room
  const addSubRoom = useCallback(async (
    roomId: string,
    name: string,
    type: 'chat' | 'voice' | 'media'
  ): Promise<boolean> => {
    try {
      // Get max position
      const maxPosition = subRooms.reduce((max, sr) => Math.max(max, sr.position), -1);

      const { error: insertError } = await supabase
        .from('personal_sub_rooms')
        .insert({
          room_id: roomId,
          name,
          type,
          position: maxPosition + 1,
        });

      if (insertError) {
        console.error('Error adding sub-room:', insertError);
        setError(insertError.message);
        return false;
      }

      await loadSubRooms(roomId);
      return true;
    } catch (err) {
      console.error('Error in addSubRoom:', err);
      return false;
    }
  }, [subRooms, loadSubRooms]);

  // Remove a sub-room
  const removeSubRoom = useCallback(async (subRoomId: string): Promise<boolean> => {
    try {
      const subRoom = subRooms.find(sr => sr.id === subRoomId);
      if (!subRoom) return false;

      const { error: deleteError } = await supabase
        .from('personal_sub_rooms')
        .delete()
        .eq('id', subRoomId);

      if (deleteError) {
        console.error('Error removing sub-room:', deleteError);
        setError(deleteError.message);
        return false;
      }

      await loadSubRooms(subRoom.room_id);
      return true;
    } catch (err) {
      console.error('Error in removeSubRoom:', err);
      return false;
    }
  }, [subRooms, loadSubRooms]);

  // Invite a user (uses RPC function)
  const inviteUser = useCallback(async (roomId: string, inviteeId: string): Promise<boolean> => {
    try {
      const { error: rpcError } = await supabase.rpc('invite_to_personal_room', {
        p_room_id: roomId,
        invitee_id: inviteeId,
      });

      if (rpcError) {
        console.error('Error inviting user:', rpcError);
        setError(rpcError.message);
        return false;
      }

      await loadRooms();
      return true;
    } catch (err) {
      console.error('Error in inviteUser:', err);
      return false;
    }
  }, [loadRooms]);

  // Remove invite (uses RPC function)
  const removeInvite = useCallback(async (roomId: string, userToRemove: string): Promise<boolean> => {
    try {
      const { error: rpcError } = await supabase.rpc('remove_from_personal_room', {
        p_room_id: roomId,
        user_to_remove: userToRemove,
      });

      if (rpcError) {
        console.error('Error removing invite:', rpcError);
        setError(rpcError.message);
        return false;
      }

      await loadRooms();
      return true;
    } catch (err) {
      console.error('Error in removeInvite:', err);
      return false;
    }
  }, [loadRooms]);

  // Send a message
  const sendMessage = useCallback(async (subRoomId: string, content: string): Promise<boolean> => {
    if (!userId || !content.trim()) return false;

    try {
      const { error: insertError } = await supabase
        .from('personal_room_messages')
        .insert({
          sub_room_id: subRoomId,
          user_id: userId,
          content: content.trim(),
        });

      if (insertError) {
        console.error('Error sending message:', insertError);
        setError(insertError.message);
        return false;
      }

      return true;
    } catch (err) {
      console.error('Error in sendMessage:', err);
      return false;
    }
  }, [userId]);

  // Load messages for a sub-room
  const loadMessages = useCallback(async (
    subRoomId: string,
    limit: number = 50
  ): Promise<PersonalRoomMessage[]> => {
    try {
      const { data, error: loadError } = await supabase
        .from('personal_room_messages')
        .select(`
          *,
          profiles:user_id (
            username,
            avatar_url
          )
        `)
        .eq('sub_room_id', subRoomId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (loadError) {
        console.error('Error loading messages:', loadError);
        return [];
      }

      // Return in chronological order
      return (data || []).reverse() as PersonalRoomMessage[];
    } catch (err) {
      console.error('Error in loadMessages:', err);
      return [];
    }
  }, []);

  // Refresh all data
  const refresh = useCallback(async () => {
    await loadRooms();
    if (myRoom) {
      await loadSubRooms(myRoom.id);
    }
  }, [loadRooms, loadSubRooms, myRoom]);

  return {
    myRoom,
    invitedRooms,
    subRooms,
    loading,
    error,
    createRoom,
    updateRoom,
    deleteRoom,
    loadSubRooms,
    addSubRoom,
    removeSubRoom,
    inviteUser,
    removeInvite,
    sendMessage,
    loadMessages,
    refresh,
  };
}
